import { api } from '../api.js';
import { el, fragment, icon } from '../dom.js';
import { t, ACCENT_LABELS, ACCENT_NAMES } from '../i18n.js';
import { institutionScope, institutionHead, NoAccessError, accessDenied } from './institution-scope.js';

const COPY = 'M8 8h14v14H8zM4 16V4h10v2H6v10z';
const CHECK = 'M20 6 9 17l-5-5';

// Five decisions, not fifty. A venue that has to design something will not
// finish, and the page's job is to look like them and load fast on mobile
// data — not to be a website.
//
// The preview is a real iframe of the real page at phone width. A mock-up
// would drift from the page it claims to show, and this one cannot: it is
// the page. That is also why this stopped being a dialog — the one surface
// whose whole point is how it looks had its preview behind something you
// dismiss to see the thing you were previewing.
export async function renderPage({ params }) {
  let scope;
  try {
    scope = await institutionScope(params[0]);
  } catch (error) {
    if (!(error instanceof NoAccessError)) throw error;
    return accessDenied();
  }

  const account = scope.account;

  // Identity, design and the team are owner-only on the API. Rendering the
  // form anyway would offer an editor fields that fill in and then 403.
  const detail = await api.get(`/accounts/${account.id}`);
  if (!detail.canManage) {
    return fragment([
      institutionHead(scope, 'page'),
      el('p', { class: 'card-meta' }, t.ownerOnly),
    ]);
  }
  // The second gate, and it needs its own answer rather than being folded into
  // the one above. `requireVerifiedDesign` refuses the save on an unclaimed
  // account, so rendering the editor here let an owner design a whole page and
  // meet a 403 at the end — the same shape as an editor being offered a form
  // that 403s, which this file's first check already exists to prevent.
  //
  // The fix for the two is a different sentence, which is why the flags stay
  // two: ask an owner, versus claim the page.
  if (!detail.verified) {
    return fragment([
      institutionHead(scope, 'page'),
      el('p', { class: 'card-meta' }, t.designNeedsClaim),
      el('p', {}, el('a', { class: 'button button-quiet', href: `/panel/antrag?venue=${account.slug}` },
        t.designClaimLink)),
    ]);
  }

  const [page, series] = await Promise.all([
    api.get(`/accounts/${account.id}/page`),
    api.get(`/accounts/${account.id}/series`),
  ]);

  const mode = radioGroup('pf-mode', [
    ['dark', t.themeDark],
    ['light', t.themeLight],
  ], page.mode);

  const accent = el('div', { class: 'accent-picker' },
    ACCENT_NAMES.map((name) => {
      const input = el('input', { type: 'radio', name: 'pf-accent', value: name, id: `pf-accent-${name}` });
      input.checked = page.accent === name;
      return el('label', { class: 'accent-swatch', for: `pf-accent-${name}`, dataset: { accent: name } },
        [input, el('span', {}, ACCENT_LABELS[name])]);
    }));

  const defaultView = radioGroup('pf-view', [
    ['list', t.viewList],
    ['calendar', t.viewCalendar],
  ], page.defaultView);

  const months = el('select', { id: 'pf-months' },
    [1, 2, 3, 6, 12, 18].map((n) => el('option', { value: n }, `${n} ${n === 1 ? t.month : t.months}`)));
  months.value = String(page.months);

  // Unticked means "everything", so a venue that never opens this page does
  // not end up with a blank bio link.
  const chosen = new Set(page.seriesIds);
  const picks = el('div', { class: 'series-picks' }, series.map((row) => {
    const box = el('input', { type: 'checkbox', id: `pf-s-${row.id}`, value: row.id });
    box.checked = chosen.size === 0 || chosen.has(row.id);
    return el('label', { class: 'check', for: `pf-s-${row.id}` }, [box, el('span', {}, row.title)]);
  }));

  const preview = el('iframe', {
    class: 'page-preview',
    src: page.url,
    title: t.preview,
    loading: 'lazy',
  });

  const error = el('p', { class: 'pub-error', role: 'alert' });
  error.hidden = true;

  const status = el('p', { class: 'cal-hint', role: 'status' });
  status.hidden = true;

  const save = el('button', { class: 'button', type: 'submit' }, t.save);
  const copy = el('button', {
    class: 'icon-button', type: 'button',
    'aria-label': t.copyLink, title: t.copyLink,
  }, icon(COPY));

  const form = el('form', { class: 'form form-wide page-form' }, [
    el('p', { class: 'card-meta' }, [
      el('a', { href: page.url, target: '_blank', rel: 'noopener' }, page.url),
      ' ',
      copy,
    ]),
    error,
    status,
    el('div', { class: 'page-form-grid' }, [
      el('div', { class: 'page-form-fields' }, [
        labelled(t.theme, mode.node),
        labelled(t.accent, accent),
        labelled(t.defaultView, defaultView.node),
        labelled(t.howFarAhead, months),
        labelled(t.whichSeries, picks),
        el('div', { class: 'row' }, [save]),
      ]),
      el('div', { class: 'page-form-preview' }, [
        el('span', { class: 'rec-label' }, t.preview),
        preview,
      ]),
    ]),
  ]);

  copy.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(page.url);
      copy.replaceChildren(icon(CHECK));
      copy.setAttribute('aria-label', t.copied);
      copy.title = t.copied;
      setTimeout(() => {
        copy.replaceChildren(icon(COPY));
        copy.setAttribute('aria-label', t.copyLink);
        copy.title = t.copyLink;
      }, 1500);
    } catch {
      // Clipboard access can be refused; the URL is on screen either way.
      copy.setAttribute('aria-label', t.copyFailed);
      copy.title = t.copyFailed;
    }
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    save.disabled = true;

    const all = [...picks.querySelectorAll('input')];
    const selected = all.filter((box) => box.checked).map((box) => Number(box.value));

    try {
      await api.patch(`/accounts/${account.id}/page`, {
        mode: mode.value(),
        accent: accent.querySelector('input:checked')?.value ?? null,
        defaultView: defaultView.value(),
        months: Number(months.value),
        // All ticked is the same as none: store the empty set so adding a new
        // series later shows up without the venue having to come back here.
        seriesIds: selected.length === all.length ? [] : selected,
      });
      // Reload the iframe rather than the page: the preview is the page.
      preview.src = `${page.url}?t=${Date.now()}`;
      error.hidden = true;
      status.textContent = t.saved;
      status.hidden = false;
    } catch {
      error.textContent = t.saveFailed;
      error.hidden = false;
    } finally {
      save.disabled = false;
    }
  });

  return fragment([institutionHead(scope, 'page'), form]);
}

function radioGroup(name, options, current) {
  const inputs = options.map(([value, label]) => {
    const input = el('input', { type: 'radio', name, value, id: `${name}-${value}` });
    input.checked = value === current;
    return el('label', { class: 'check', for: `${name}-${value}` }, [input, el('span', {}, label)]);
  });
  const node = el('div', { class: 'radio-row' }, inputs);
  return {
    node,
    value: () => node.querySelector('input:checked')?.value ?? options[0][0],
  };
}

function labelled(label, control) {
  return el('div', { class: 'field' }, [el('span', { class: 'rec-label' }, label), control]);
}
